import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Download,
  Refresh,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  AttachMoney,
  CalendarToday,
  Description,
  AccountBalance,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { settlementService } from '../services/api';

interface Settlement {
  id: string;
  batchNumber: string;
  partnerId: string;
  netAmount: number;
  currency: string;
  status: 'PENDING' | 'APPROVED' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'REJECTED';
  transactionCount: number;
  settlementDate: string;
  createdAt: string;
  approvedAt?: string;
  completedAt?: string;
  bankAccountNumber?: string;
  bankReference?: string;
  failureReason?: string;
}

interface PartnerSettlementsProps {
  partnerId: string;
}

const PartnerSettlements: React.FC<PartnerSettlementsProps> = ({ partnerId }) => {
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Statistics
  const [stats, setStats] = useState({
    totalSettled: 0,
    pendingAmount: 0,
    lastSettlement: null as Date | null,
    nextSettlement: null as Date | null,
  });

  useEffect(() => {
    fetchSettlements();
  }, [partnerId, page, rowsPerPage]);

  const fetchSettlements = async () => {
    setLoading(true);
    try {
      const params = {
        partnerId,
        page,
        size: rowsPerPage,
      };

      const response = await settlementService.getSettlements(params);
      setSettlements(response.content || []);
      setTotalCount(response.totalElements || 0);

      // Calculate statistics
      calculateStats(response.content || []);
    } catch (error) {
      console.error('Failed to fetch settlements:', error);
      setSettlements([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (settlements: Settlement[]) => {
    const completed = settlements.filter(s => s.status === 'COMPLETED');
    const pending = settlements.filter(s => ['PENDING', 'APPROVED', 'PROCESSING'].includes(s.status));

    const totalSettled = completed.reduce((sum, s) => sum + (s.netAmount ?? 0), 0);
    const pendingAmount = pending.reduce((sum, s) => sum + (s.netAmount ?? 0), 0);

    const lastCompleted = completed.sort((a, b) =>
      new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime()
    )[0];

    setStats({
      totalSettled,
      pendingAmount,
      lastSettlement: lastCompleted ? new Date(lastCompleted.completedAt!) : null,
      nextSettlement: null, // Would need to calculate based on settlement frequency
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle color="success" fontSize="small" />;
      case 'FAILED':
      case 'REJECTED':
        return <Cancel color="error" fontSize="small" />;
      case 'PENDING':
      case 'APPROVED':
      case 'PROCESSING':
        return <HourglassEmpty color="warning" fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
      case 'REJECTED':
        return 'error';
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
      case 'PROCESSING':
        return 'info';
      default:
        return 'default';
    }
  };

  const downloadSettlement = async (settlementId: string) => {
    try {
      const blob = await settlementService.downloadSettlementFile(settlementId, 'csv');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `settlement-${settlementId}.csv`;
      a.click();
    } catch (error) {
      console.error('Failed to download settlement:', error);
    }
  };

  return (
    <Box>
      {/* Statistics Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Total Settled
                  </Typography>
                  <Typography variant="h5">
                    ${stats.totalSettled.toLocaleString()}
                  </Typography>
                </Box>
                <AttachMoney color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Pending Amount
                  </Typography>
                  <Typography variant="h5">
                    ${stats.pendingAmount.toLocaleString()}
                  </Typography>
                </Box>
                <HourglassEmpty color="warning" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Last Settlement
                  </Typography>
                  <Typography variant="body1">
                    {stats.lastSettlement ? format(stats.lastSettlement, 'MMM dd, yyyy') : 'N/A'}
                  </Typography>
                </Box>
                <CalendarToday color="info" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Total Settlements
                  </Typography>
                  <Typography variant="h5">
                    {totalCount}
                  </Typography>
                </Box>
                <AccountBalance color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Settlement History</Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchSettlements}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<Download />}>
            Export All
          </Button>
        </Box>
      </Box>

      {/* Settlements Table */}
      <TableContainer component={Paper}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Settlement Date</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Transactions</TableCell>
              <TableCell>Bank Reference</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settlements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {loading ? 'Loading...' : 'No settlements found'}
                </TableCell>
              </TableRow>
            ) : (
              settlements.map((settlement) => (
                <TableRow key={settlement.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(settlement.settlementDate), 'MMM dd, yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {settlement.batchNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getStatusIcon(settlement.status)}
                      <Chip
                        label={settlement.status}
                        color={getStatusColor(settlement.status)}
                        size="small"
                      />
                    </Box>
                    {settlement.failureReason && (
                      <Tooltip title={settlement.failureReason}>
                        <Typography variant="caption" color="error" display="block">
                          Rejected
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      ${(settlement.netAmount ?? 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {settlement.transactionCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {settlement.bankReference || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(settlement.createdAt), 'MMM dd, HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Download Settlement Report">
                      <IconButton
                        size="small"
                        onClick={() => downloadSettlement(settlement.id)}
                      >
                        <Download />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {settlements.filter(s => s.status === 'PENDING').length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          You have {settlements.filter(s => s.status === 'PENDING').length} pending settlement(s) awaiting approval.
        </Alert>
      )}
    </Box>
  );
};

export default PartnerSettlements;